import { AttributeValueType } from 'Types/Attribute.types'
import {
    AttributeDefinitionType,
    NotificationPropsType,
    RatingsOnLinearConversationDataType,
    TaskAttribute,
} from 'Types/Tasks.types'
import { AttributeContext } from '../AttributeManager'
import {
    AttributeGroupConfigExtended,
    AttributeGroupEvent,
    BaseAttributeGroup,
} from './BaseAttributeGroup'
import { createReactElement } from 'shared/utils/react.utils'
import { RatingsAttributeBody } from 'Components/tasks/RatingsAttribute/RatingsAttributeBody'

export type RatingsTaskProps = {
    getClickedResponseId: () => string
    getHoveredResponseId: () => string
}

export class RatingsAttributeGroup extends BaseAttributeGroup<RatingsTaskProps> {
    groupKey = AttributeDefinitionType.RESPONSE_RATING
    attributeDefinitionType = AttributeDefinitionType.RESPONSE_RATING

    constructor(attributeContext: AttributeContext, config: AttributeGroupConfigExtended) {
        super(attributeContext, config)
        this.build()
    }

    validate = async () => {
        const isRatingsAttributesFormValid =
            !this.attributes.length || (await this.form?.validateFields().catch(() => false))
        if (!isRatingsAttributesFormValid) {
            this.attributeContext.studio.inputManager.addViolation({
                id: 'rating-attributes',
                type: 'error',
                message: 'Please fill all the required response ratings',
            })
            this.attributeContext.studio.workbench.enqueueUiUpdate(ui => {
                ui.rightSidebar.changeActiveItemId('ratings-attribute')
            })
            return false
        }

        this.attributeContext.studio.inputManager.removeViolation('rating-attributes', 'error')
        return true
    }

    private initRatingsAttribute(
        attrName: string,
        val: NonNullable<AttributeValueType>,
        ratingsAttributes: TaskAttribute[],
        responseId: string
    ) {
        const data = this.attributeContext.taskState.getDraftOutputContainer()?.data
            .data as RatingsOnLinearConversationDataType
        if (!data) {
            return
        }

        if (!data?.responses_ratings) {
            data.responses_ratings = []
        }

        if (!data.responses_ratings.find(rating => rating.response_id === responseId)) {
            data.responses_ratings.push({
                ratings: {},
                response_id: responseId,
            })
        }

        data.responses_ratings.find(rating => rating.response_id === responseId)!.ratings[
            attrName
        ] = {
            data: {
                value: val,
                value_type: ratingsAttributes.find(attr => attr.name === attrName)!.value_definition
                    .value_type,
            },
        }
    }

    private updateRatingsAttribute(parameters: {
        attrName: string
        attrValue: AttributeValueType
        responseId: string
    }) {
        const { attrName, attrValue, responseId } = parameters
        const config = this.attributeContext.taskConfig
        const ratingsAttributes = config.assignment.task.attributes.filter(
            attribute => attribute.type === AttributeDefinitionType.RESPONSE_RATING
        )
        const data = this.attributeContext.taskState.getDraftOutputContainer()?.data
            .data as RatingsOnLinearConversationDataType
        if (!data) {
            return
        }

        const container =
            data.responses_ratings?.find(item => item.response_id === responseId)?.ratings ?? {}

        if (attrValue === undefined) {
            if (container[attrName]) {
                delete container[attrName]
            }
        } else {
            if (container[attrName]) {
                container[attrName].data.value = attrValue
            } else {
                this.initRatingsAttribute(
                    attrName,
                    attrValue as NonNullable<AttributeValueType>,
                    ratingsAttributes,
                    responseId
                )
            }
        }
    }

    build() {
        const config = this.attributeContext.taskConfig
        const globalAttributes = config.assignment.task.attributes.filter(
            attribute => attribute.type === AttributeDefinitionType.EXTRA_INFO
        )
        const data = this.attributeContext.taskState.getDraftOutputContainer()?.data
            .data as RatingsOnLinearConversationDataType
        if (!data) {
            return
        }

        this.element = createReactElement(RatingsAttributeBody, {
            ratingsAttributes: this.attributes,
            globalAttributes,
            globalValues: data.extra_info,
            onUpdateAttribute: (attrName: string, val: AttributeValueType, responseId: string) => {
                this.updateRatingsAttribute({
                    attrName,
                    attrValue: val,
                    responseId,
                })
                this.attributeContext.saveTask({
                    onComplete: (notifications?: NotificationPropsType) =>
                        this.attributeContext.studio.exitTask(notifications),
                })
                this.render()
                this.emit(AttributeGroupEvent.Change)
                data.responses_ratings?.forEach(response => {
                    this.updateAreMandatoryAttributesFilled(response.ratings)
                })
            },
            responseIds:
                config.assignment.task.input_container.data.data?.responses?.map(
                    response => response.id
                ) ?? [],
            values: data.responses_ratings,
            attributesOnUI: this.attributeContext.attributesOnUI,
            setForm: form => {
                this.form = form
            },
            getClickedResponseId: this.taskProps?.getClickedResponseId,
            getHoveredResponseId: this.taskProps?.getHoveredResponseId,
            isReadOnly: () => this.attributeContext.task.isReadOnly,
        })
    }

    removeAllAttributes() {
        const data = this.attributeContext.taskState.getDraftOutputContainer()?.data
            .data as RatingsOnLinearConversationDataType
        if (!data) {
            return
        }

        data.responses_ratings = []
    }
}
